import { Fragment, useContext, useEffect, useState } from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubCard,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Link, useNavigate } from 'react-router-dom';
import { ApiPortalCategory } from './ApiPortalTypes';
import { slugifyHyphen } from '../../util/stringFormat';
import { Progress } from '@backstage/core-components';
import { ApiPortalContext } from '../../contexts/ApiPortalContext';
import ApiPortalTabbedPage from './ApiPortalTabbedPage';
import { NewAnnouncementBanner } from '@backstage-community/plugin-announcements';

type ApiPortalOrgProps = {
  isMainPage: boolean;
};

/**
 * A component to render an API Portal org.
 * @param props the props of the component
 * @param props.isMainPage a boolean to determine if the page is the main view of the tabbed page
 */
export const ApiPortalOrg = (props: ApiPortalOrgProps) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<ApiPortalCategory[]>([]);
  const { org, loading, error, formattedOrgName } =
    useContext(ApiPortalContext);

  useEffect(() => {
    if (org && org.categoriesPaged?.categories) {
      const mappedCategories = org.categoriesPaged?.categories
        ?.filter(
          category =>
            (category.apiEventsPaged.totalCount > 0 ||
              category.apiSpecsPaged.totalCount > 0 ||
              category.apiScenariosPaged.totalCount > 0) &&
            category.name !== 'Data Feed',
        )
        .map(category => ({
          ...category,
          logo: `data:image/svg+xml;base64,${Buffer.from(
            category.logo,
          ).toString('base64')}`,
        }));
      setCategories(mappedCategories);
    }
  }, [org]);

  const handleCardClick = (category: string) => {
    navigate(`/api-portals/${org._normalizedName}/${slugifyHyphen(category)}`);
  };

  const handleBackClick = () => {
    navigate(`/api-portals`);
  };

  const content = (
    <>
      {org && org._normalizedName === 'connected-living' && (
        <DevHubGrid container>
          <DevHubGrid item md={12}>
            <NewAnnouncementBanner category="connected-living" />
          </DevHubGrid>
        </DevHubGrid>
      )}
      <DevHubBox display="flex">
        <DevHubGrid container spacing={2}>
          {categories.map(category => (
            <DevHubGrid item xs={12} md={6} lg={4} key={category.name}>
              <Link
                to={`/api-portals/${org._normalizedName}/${slugifyHyphen(
                  category.name,
                )}`}
                style={{ textDecoration: 'none' }}
              >
                <DevHubCard
                  onClick={() => handleCardClick(category.name)}
                  density="cozy"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="blue"
                  bodyPosition="middle"
                  subTitle=""
                  body={
                    <Fragment>
                      <DevHubBox
                        tbPadding="large"
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <img
                          src={category.logo}
                          height="40px"
                          alt={category.name}
                        />
                      </DevHubBox>
                      <DevHubBox
                        tbPadding="medium"
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <DevHubTypography variant="h5">
                          {category.name}
                        </DevHubTypography>
                        <DevHubTypography>
                          {category.description}
                        </DevHubTypography>
                      </DevHubBox>
                    </Fragment>
                  }
                />
              </Link>
            </DevHubGrid>
          ))}
        </DevHubGrid>
      </DevHubBox>
    </>
  );

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">
        Error loading API Portal organization data: {error.message}
      </DevHubAlert>
    );
  }

  return props.isMainPage ? (
    <ApiPortalTabbedPage
      formattedOrgName={formattedOrgName}
      apiManagementContent={content}
      handleBackClick={handleBackClick}
    />
  ) : (
    content
  );
};
